import React, { useState, useEffect } from "react";

const Services = () => {
  const [activeTab, setActiveTab] = useState("Health Sciences");
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    setAnimate(true);
    const timer = setTimeout(() => setAnimate(false), 800);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const data = {
    "Health Sciences": [
      {
        title: "Medical Research & Innovation",
        desc: "Advancing fundamental health sciences with cutting-edge research in physiology, anatomy, and molecular biology to improve disease prevention and treatment.",
        color: "#e3f2fd",
      },
      {
        title: "Diagnostics & Imaging",
        desc: "Enabling accurate and early disease detection through advanced imaging technologies such as MRI, CT scans, and AI-powered diagnostic tools.",
        color: "#fff3e0",
      },
      {
        title: "Public Health & Preventive Care",
        desc: "Promoting community well-being with digital health monitoring, epidemiology tools, and preventive medicine strategies powered by data analytics.",
        color: "#f3e5f5",
      },
    ],
    "Healthcare Technology": [
      {
        title: "Telemedicine & Remote Care",
        desc: "Transforming access to healthcare with virtual consultations, e-health platforms, and remote patient monitoring systems.",
        color: "#e8f5e9",
      },
      {
        title: "Wearable Health Devices",
        desc: "Improving personal health tracking with smartwatches, fitness bands, and IoT-based wearable devices for real-time monitoring.",
        color: "#fce4ec",
      },
      {
        title: "Electronic Health Records (EHR)",
        desc: "Digitizing patient records for secure, efficient, and seamless healthcare delivery across hospitals and clinics.",
        color: "#ede7f6",
      },
    ],
    "Future of Medicine": [
      {
        title: "Artificial Intelligence in Healthcare",
        desc: "Leveraging AI for predictive analytics, drug discovery, and personalized treatment recommendations.",
        color: "#fff9c4",
      },
      {
        title: "Genomics & Precision Medicine",
        desc: "Unlocking genetic insights to design treatments tailored to individual patient profiles and hereditary conditions.",
        color: "#c8e6c9",
      },
      {
        title: "Robotics & Surgery",
        desc: "Enhancing surgical precision and efficiency with robotic-assisted operations and automated clinical procedures.",
        color: "#ffcdd2",
      },
    ],
  };

  const styles = {
    container: {
      padding: "clamp(16px, 4vw, 40px)",
      fontFamily: "Arial, sans-serif",
      width: "100%",
      margin: "0 auto",
      backgroundColor: "white",
      boxSizing: "border-box",
    },
    heading: {
      fontSize: "28px",
      fontWeight: "bold",
      marginBottom: "clamp(16px, 3vw, 20px)",
      textAlign: "left",
      letterSpacing: "0px",
      padding: "0 clamp(16px, 4vw, 0px)",
      color:"black",
    },
    tabs: {
      display: "flex",
      gap: "clamp(12px, 3vw, 30px)",
      marginBottom: "clamp(20px, 4vw, 30px)",
      flexWrap: "wrap",
      padding: "0 clamp(16px, 4vw, 0px)",
    },
    tab: (isActive) => ({
      fontSize: "clamp(14px, 2.5vw, 18px)",
      fontWeight: isActive ? "bold" : "normal",
      cursor: "pointer",
      borderBottom: isActive ? "2px solid red" : "none",
      color: isActive ? "#000" : "gray",
      paddingBottom: "5px",
      transition: "0.3s",
      whiteSpace: "nowrap",
    }),
    cards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
      gap: "clamp(16px, 3vw, 20px)",
      padding: "0 clamp(16px, 4vw, 0px)",
    },
    card: (bgColor, index) => ({
      background: bgColor,
      padding: "clamp(12px, 3vw, 20px)",
      borderRadius: "8px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      transform: animate
        ? `translateY(${index % 2 === 0 ? "20px" : "-20px"})`
        : "translateY(0)",
      opacity: animate ? 0 : 1,
      animation: animate ? `waveIn 0.8s ease forwards ${index * 0.2}s` : "none",
      minHeight: "clamp(180px, 35vw, 200px)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
    }),
    cardTitle: {
      fontSize: "clamp(16px, 3.5vw, 20px)",
      fontWeight: "bold",
      marginBottom: "clamp(8px, 1.5vw, 15px)",
      letterSpacing: "0px",
      lineHeight: "1.3",
    },
    cardText: {
      fontSize: "clamp(13px, 2.2vw, 16px)",
      lineHeight: "1.4",
      marginBottom: "12px",
      flexGrow: 1,
      textAlign: "left",
    },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Health Science & Technology Services</h1>

      {/* Tabs */}
      <div style={styles.tabs}>
        {Object.keys(data).map((tab) => (
          <div
            key={tab}
            style={styles.tab(activeTab === tab)}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </div>
        ))}
      </div>

      {/* Cards */}
      <div style={styles.cards}>
        {data[activeTab].map((item, index) => (
          <div key={index} style={styles.card(item.color, index)}>
            <div>
              <h2 style={styles.cardTitle}>{item.title}</h2>
              <p style={styles.cardText}>{item.desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Inline keyframes */}
      <style>
        {`
          @keyframes waveIn {
            0% { transform: translateY(40px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          
          @media (max-width: 480px) {
            .services-container {
              padding: 12px;
            }
            
            .services-heading {
              font-size: 22px;
            }
            
            .services-tabs {
              gap: 10px;
              justify-content: center;
            }
            
            .services-tab {
              font-size: 13px;
            }
            
            .services-cards {
              grid-template-columns: 1fr;
              gap: 12px;
            }
            
            .services-card {
              min-height: 160px;
              padding: 12px;
            }
            
            .services-card-title {
              font-size: 15px;
            }
            
            .services-card-text {
              font-size: 12px;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Services;